import React, { useEffect, useState, useRef } from "react";

export default function AboutStructured({ className = "" }) {
  const [visibleCards, setVisibleCards] = useState([]);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const observerRef = useRef(null);

  const items = [
    {
      id: 0,
      year: "",
      label: "About us",
      title: "AfricaMed Business Forum",
      summary:
        "AfricaMed Business Forum: A Continental Platform for Investment and Impact Since its creation, the AfricaMed Business Forum has established itself as the leading African economic meeting point, bringing together governments, investors, companies, institutions, researchers, and experts around a common goal: to accelerate Africa's structural transformation through productive investment and South South cooperation.",
      icon: "🌍"
    },
    {
      id: 1,
      year: "2023",
      label: "First Edition",
      title: "Tangier Morocco",
      summary:
        "Launch of the concept with more than 300 leaders and 50 investment projects presented. This inaugural edition marked the beginning of a transformative journey for Africa's economic landscape.",
      icon: "🚀"
    },
    {
      id: 2,
      year: "2025",
      label: "Second Edition",
      title: "Casablanca Morocco",
      summary:
        "Consolidation of the model with 200 qualified participants and 65 countries represented and the launch of Club AfricaMed a permanent economic community.",
      icon: "🤝"
    },
    {
      id: 3,
      year: "2026",
      label: "Third Edition",
      title: "Casablanca Morocco",
      summary:
        "A strategic turning point placing sport at the center as a driver of industrialization cohesion and the continent's economic transformation.",
      icon: "⚽"
    }
  ];

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = parseInt(entry.target.dataset.id);
            setVisibleCards((prev) => [...new Set([...prev, id])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const cards = document.querySelectorAll(".timeline-card");
    cards.forEach((card) => observerRef.current.observe(card));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <section id="about" className={`about-section ${className}`}>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes drawLine {
          from {
            height: 0;
          }
          to {
            height: 100%;
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(245, 158, 11, 0);
          }
        }

        .about-section {
          min-height: 100vh;
          padding: 80px 20px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
          position: relative;
          overflow: hidden;
        }

        .about-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 20% 50%, rgba(245, 158, 11, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 80%, rgba(245, 158, 11, 0.05) 0%, transparent 50%);
          pointer-events: none;
        }

        .about-container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .about-header {
          text-align: center;
          margin-bottom: 80px;
          animation: fadeInUp 1s ease-out;
        }

        .about-title {
          font-size: clamp(42px, 6vw, 72px);
          font-weight: 900;
          background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 20px;
          letter-spacing: -2px;
          text-transform: uppercase;
        }

        .about-subtitle {
          font-size: clamp(18px, 2vw, 24px);
          color: #cbd5e1;
          font-weight: 300;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .timeline-container {
          position: relative;
          padding: 40px 0;
        }

        .timeline-line {
          position: absolute;
          left: 50%;
          top: 0;
          width: 4px;
          height: 0;
          background: linear-gradient(180deg, #f59e0b 0%, #fbbf24 100%);
          transform: translateX(-50%);
          animation: drawLine 2s ease-out forwards;
          box-shadow: 0 0 20px rgba(245, 158, 11, 0.5);
        }

        @media (max-width: 768px) {
          .timeline-line {
            left: 30px;
          }
        }

        .timeline-card {
          display: flex;
          align-items: center;
          margin-bottom: 80px;
          opacity: 0;
          position: relative;
        }

        .timeline-card.visible {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .timeline-card:nth-child(even) {
          flex-direction: row-reverse;
        }

        @media (max-width: 768px) {
          .timeline-card,
          .timeline-card:nth-child(even) {
            flex-direction: row;
            padding-left: 60px;
          }
        }

        .timeline-content {
          flex: 1;
          padding: 0 60px;
        }

        @media (max-width: 768px) {
          .timeline-content {
            padding: 0 20px;
          }
        }

        .timeline-card:nth-child(odd) .card-inner {
          animation: fadeInRight 0.8s ease-out forwards;
        }

        .timeline-card:nth-child(even) .card-inner {
          animation: fadeInLeft 0.8s ease-out forwards;
        }

        .card-inner {
          background: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.8) 100%);
          border-radius: 20px;
          padding: 40px;
          border: 2px solid rgba(245, 158, 11, 0.3);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          position: relative;
          overflow: hidden;
        }

        .card-inner::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(245, 158, 11, 0.1), transparent);
          transition: left 0.5s;
        }

        .card-inner:hover::before {
          left: 100%;
        }

        .card-inner:hover {
          transform: translateY(-10px) scale(1.02);
          border-color: rgba(245, 158, 11, 0.6);
          box-shadow: 0 20px 60px rgba(245, 158, 11, 0.3);
        }

        .timeline-dot {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          border: 5px solid #0f172a;
          box-shadow: 0 0 30px rgba(245, 158, 11, 0.6);
          z-index: 10;
          animation: scaleIn 0.6s ease-out forwards, pulse 2s infinite;
        }

        @media (max-width: 768px) {
          .timeline-dot {
            left: 30px;
          }
        }

        .card-year {
          position: absolute;
          top: -15px;
          right: 30px;
          background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
          color: #0f172a;
          font-size: 14px;
          font-weight: 900;
          padding: 8px 20px;
          border-radius: 30px;
          box-shadow: 0 5px 15px rgba(245, 158, 11, 0.4);
          letter-spacing: 2px;
        }

        .card-label {
          font-size: 14px;
          color: #f59e0b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 12px;
        }

        .card-title {
          font-size: clamp(24px, 3vw, 32px);
          color: #ffffff;
          font-weight: 800;
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .card-summary {
          font-size: 16px;
          color: #cbd5e1;
          line-height: 1.8;
          text-align: justify;
        }

        .closing-statement {
          position: relative;
          margin-top: 80px;
          padding: 60px 50px;
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.05) 100%);
          border-radius: 20px;
          border: 2px solid rgba(245, 158, 11, 0.3);
          text-align: center;
          animation: fadeInUp 1s ease-out 1s both;
          overflow: hidden;
        }

        .closing-statement p {
          font-size: clamp(16px, 2vw, 18px);
          color: #e2e8f0;
          line-height: 1.8;
          margin: 0;
          text-align: justify;
          max-width: 900px;
          margin: 0 auto;
          position: relative;
          z-index: 5;
        }

        .soccer-animation {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }

        .kick-foot {
          animation: kickMotion 4s ease-in-out infinite;
        }

        @keyframes kickMotion {
          0%, 100% {
            transform: rotate(0deg);
          }
          8% {
            transform: rotate(-25deg);
          }
          15%, 100% {
            transform: rotate(0deg);
          }
        }

        .soccer-ball {
          animation: ballFlight 4s ease-in-out infinite;
        }

        @keyframes ballFlight {
          0%, 15% {
            opacity: 0;
            transform: translate(0, 0) scale(1);
          }
          18% {
            opacity: 1;
          }
          50% {
            opacity: 1;
            transform: translate(450px, -120px) scale(0.7) rotate(720deg);
          }
          82% {
            opacity: 1;
            transform: translate(900px, 0) scale(1) rotate(1440deg);
          }
          85%, 100% {
            opacity: 0;
            transform: translate(900px, 0) scale(1) rotate(1440deg);
          }
        }

        .ball-trail {
          position: absolute;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: radial-gradient(circle, #fbbf24, transparent);
          opacity: 0;
          animation: trailFade 4s ease-in-out infinite;
        }

        @keyframes trailFade {
          0%, 15% {
            opacity: 0;
          }
          20%, 50% {
            opacity: 0.6;
          }
          70%, 100% {
            opacity: 0;
          }
        }

        .trail-1 { animation-delay: 0.1s; }
        .trail-2 { animation-delay: 0.2s; }
        .trail-3 { animation-delay: 0.3s; }
        .trail-4 { animation-delay: 0.4s; }

        .sparkle-effect {
          position: absolute;
          font-size: 20px;
          animation: sparkleFloat 4s ease-in-out infinite;
          opacity: 0;
        }

        @keyframes sparkleFloat {
          0%, 15%, 85%, 100% {
            opacity: 0;
            transform: translateY(0) scale(0.5);
          }
          50% {
            opacity: 1;
            transform: translateY(-30px) scale(1.2);
          }
        }

        .sparkle-1 { left: 30%; top: 20%; animation-delay: 1.2s; }
        .sparkle-2 { left: 50%; top: 10%; animation-delay: 1.5s; }
        .sparkle-3 { left: 70%; top: 25%; animation-delay: 1.8s; }

        .glow-pulse {
          animation: glowPulse 4s ease-in-out infinite;
        }
        
        @keyframes glowPulse {
          0%, 100% {
            box-shadow: 0 10px 40px rgba(251, 191, 36, 0.2);
          }
          50% {
            box-shadow: 0 10px 60px rgba(251, 191, 36, 0.4), 0 0 80px rgba(16, 185, 129, 0.3);
          }
        }
      `}</style>

      <div className="about-container">
        <div className="about-header">
          <h2 className="about-title">Our Journey</h2>
          <p className="about-subtitle">
            Building Africa's Future Through Strategic Partnerships and Innovation
          </p>
        </div>

        <div className="timeline-container">
          <div className="timeline-line"></div>

          {items.map((item, index) => (
            <div
              key={item.id}
              className={`timeline-card ${visibleCards.includes(item.id) ? 'visible' : ''}`}
              data-id={item.id}
            >
              <div className="timeline-dot">{item.icon}</div>
              <div className="timeline-content">
                <div className="card-inner">
                  {item.year && <div className="card-year">{item.year}</div>}
                  <div className="card-label">{item.label}</div>
                  <h3 className="card-title">{item.title}</h3>
                  <p className="card-summary">{item.summary}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="closing-statement glow-pulse">
          {/* Soccer Kick Animation */}
          <svg className="soccer-animation" viewBox="0 0 1000 200" preserveAspectRatio="xMidYMid meet">
            {/* Foot/Leg */}
            <g className="kick-foot" style={{ transformOrigin: '80px 160px' }}>
              {/* Leg */}
              <rect x="70" y="120" width="15" height="60" rx="7" fill="#fbbf24" opacity="0.8" />
              {/* Foot */}
              <ellipse cx="80" cy="170" rx="25" ry="12" fill="#f59e0b" opacity="0.9" />
              {/* Shoe detail */}
              <path d="M 65 165 Q 80 168 95 165" stroke="#fff" strokeWidth="2" fill="none" opacity="0.6" />
            </g>

            {/* Soccer Ball */}
            <g className="soccer-ball" style={{ transformOrigin: '100px 150px' }}>
              <circle cx="100" cy="150" r="18" fill="#fff" opacity="0.95" />
              {/* Ball pattern */}
              <path d="M 100 132 L 95 140 L 100 145 L 105 140 Z" fill="#1e293b" opacity="0.8" />
              <path d="M 100 155 L 95 160 L 100 168 L 105 160 Z" fill="#1e293b" opacity="0.8" />
              <circle cx="100" cy="150" r="18" fill="none" stroke="#fbbf24" strokeWidth="1" opacity="0.3" />
            </g>

            {/* Motion trails */}
            <g className="soccer-ball">
              <circle className="ball-trail trail-1" cx="150" cy="130" r="3" />
              <circle className="ball-trail trail-2" cx="200" cy="110" r="3" />
              <circle className="ball-trail trail-3" cx="250" cy="95" r="3" />
              <circle className="ball-trail trail-4" cx="300" cy="90" r="3" />
            </g>
          </svg>

          {/* Sparkle effects */}
          <div className="sparkle-effect sparkle-1">✨</div>
          <div className="sparkle-effect sparkle-2">⚡</div>
          <div className="sparkle-effect sparkle-3">💫</div>

          <p>
            This third edition takes place in an exceptional context Morocco host country of the 2026 Africa Cup of Nations AFCON and cohost of the 2030 FIFA World Cup with Spain and Portugal This alignment positions the Kingdom as a continental capital of sports and South South cooperation offering a symbolic and strategic framework that highlights sports as a continental driver of growth investment and international influence
          </p>
        </div>
      </div>
    </section>
  );
}
